import moment from 'moment-timezone'

import { getRandomMinuteInterval } from './utils'

import recommendations from '@/graphql/queries/analytics/recommendations.gql'
import scheduledSubmissionsLight from '@/graphql/queries/posts/scheduledSubmissionsLight.gql'

/**
 * Returns an Array of the next schedule dates for the given subreddit schedule.
 * @param {Object[]} subredditSchedule - The array of schedule objects.
 * @param {string} lastScheduledSubmissionDate - The last scheduled submission date in ISO-8601 format.
 * @returns {Array} - The array of next schedule dates.
 */
export function getScheduledDatesFromLastSubmission(
  subredditSchedule,
  lastScheduledSubmissionDate
) {
  let now = moment().seconds(0).milliseconds(0)
  let initialDate = now.clone()
  if (lastScheduledSubmissionDate) {
    let lastSubmissionDate = moment(lastScheduledSubmissionDate)
    if (lastSubmissionDate.isAfter(initialDate)) {
      initialDate = lastSubmissionDate.clone()
    }
  }

  return subredditSchedule
    .map((schedule) => {
      // Split time string in the format HH:mm
      const parts = schedule.time.split(':')
      const hour = parseInt(parts[0])
      const minutes = parseInt(parts[1])

      let date = initialDate.clone().isoWeekday(schedule.day).set({
        hour: hour,
        minute: minutes
      })
      if (date.isSameOrBefore(initialDate)) {
        date = date.add(1, 'week')
      }

      return date
    })
    .sort((a, b) => {
      return a.diff(b)
    })
}

/**
 * Returns an Array of the next schedule dates for the given subreddit schedule.
 * This function will return all scheduled dates, including any gaps, up to
 * 4 weeks after the last scheduled submission.
 * @param {Object[]} subredditSchedule - The array of schedule objects.
 * @param {Object[]} scheduledSubmissions - The array of already scheduled submissions for this subreddit.
 * @returns {Array} - The array of next schedule dates.
 */
export function getNextScheduleDates(subredditSchedule, scheduledSubmissions) {
  let now = moment().seconds(0).milliseconds(0)

  let startDate = now.clone()

  let endDate = now.clone()
  if (scheduledSubmissions.length > 0) {
    let lastScheduledDate = moment(
      scheduledSubmissions[scheduledSubmissions.length - 1].postAt
    )
    if (lastScheduledDate.isAfter(endDate)) {
      endDate = lastScheduledDate
    }
  }
  endDate = endDate.add(4, 'weeks')

  let dates = []
  let currentDate = startDate.clone()

  // Start schedule index from the next available day
  let scheduleIndex = 0
  for (let schedule of subredditSchedule) {
    const parts = schedule.time.split(':')
    const hour = parseInt(parts[0])
    const minutes = parseInt(parts[1])

    let date = currentDate.clone().isoWeekday(schedule.day).set({
      hour: hour,
      minute: minutes
    })
    if (date.isSameOrAfter(currentDate)) {
      break
    }

    scheduleIndex++
  }

  if (scheduleIndex >= subredditSchedule.length) {
    scheduleIndex = 0
  }

  while (currentDate.isBefore(endDate)) {
    // Split time string in the format HH:mm
    let schedule = subredditSchedule[scheduleIndex]
    const parts = schedule.time.split(':')
    const hour = parseInt(parts[0])
    const minutes = parseInt(parts[1])

    let date = currentDate.clone().isoWeekday(schedule.day).set({
      hour: hour,
      minute: minutes
    })
    if (date.isSameOrBefore(currentDate)) {
      date = date.add(1, 'week')
    }

    const hasScheduledSubmissionForDateAndTime = scheduledSubmissions.find(
      (submission) => moment(submission.postAt).isSame(date, 'minute')
    )
    const hasScheduledSubmissionForDate = scheduledSubmissions.find(
      (submission) => moment(submission.postAt).isSame(date, 'day')
    )
    const hasMultipleSchedulesForWeekday =
      subredditSchedule.filter((s) => s.day === schedule.day).length > 1

    if (
      !hasScheduledSubmissionForDateAndTime &&
      (!hasScheduledSubmissionForDate || hasMultipleSchedulesForWeekday)
    ) {
      dates.push(date)
    }

    scheduleIndex = (scheduleIndex + 1) % subredditSchedule.length
    currentDate = date.clone()
  }

  return dates.sort((a, b) => {
    return a.diff(b)
  })
}

// Regexes for parsing recommendation expressions.
const BEST_TODAY_REGEX = /(\d+)?best_today([+-]\d+[mh])?/
const BEST_TOMORROW_REGEX = /(\d+)?best_tomorrow([+-]\d+[mh])?/
const BEST_RELATIVE_REGEX = /(\d+)?best_(\d*)_days([+-]\d+[mh])?/
const BEST_ABSOLUTE_REGEX =
  /(\d+)?best_(\d\d\d\d)-(\d\d?)-(\d\d?)([+-]\d+[mh])?/
const BEST_RANGE_REGEX = /(\d+)?best_next_(\d*)_days([+-]\d+[mh])?/

const RECOMMENDATION_REGEXES = [
  BEST_TODAY_REGEX,
  BEST_TOMORROW_REGEX,
  BEST_RELATIVE_REGEX,
  BEST_ABSOLUTE_REGEX,
  BEST_RANGE_REGEX
]

export function validateRecommendationExpression(recommendation) {
  return RECOMMENDATION_REGEXES.some((regex) => recommendation.match(regex))
}

/**
 * Recommends a submission date based on the given recommendation expression.
 * @param {String} subreddit - The subreddit name.
 * @param {String} recommendation - The recommendation expression (eg. best_today)
 * @param {String} userTimezone - The user's timezone.
 * @param {[moment]} busyDates - The dates that are already taken by other submissions.
 *                               When present, the function will try to avoid these dates.
 * @param {Object} $apollo - The apollo client.
 * @param {Number} spacing - The minimum spacing between submissions in hours.
 * @returns {Object} - The recommended submission date and a boolean indicating whether
 *                     that date conflicts with another submission or not.
 */
export async function getPostAtFromRecommendation(
  subreddit,
  recommendation,
  userTimezone,
  busyDates,
  $apollo,
  spacing = 24
) {
  const response = await $apollo.query({
    query: recommendations,
    variables: {
      subreddit: subreddit,
      timezone: userTimezone
    }
  })
  const analytics = response.data.analytics

  // Subreddit must not exist. Return early and let validation handle this.
  if (!analytics.subreddit) {
    return
  }

  // Get the date from the recommendation
  const startDate = moment()
  let endDate
  let bestDate
  let bestDateTimes
  let offset = ''
  let recommendedIndex = 0
  const bestTodayMatch = recommendation.match(BEST_TODAY_REGEX)
  const bestTomorrowMatch = recommendation.match(BEST_TOMORROW_REGEX)
  const relativeRegexMatch = recommendation.match(BEST_RELATIVE_REGEX)
  const absoluteRegexMatch = recommendation.match(BEST_ABSOLUTE_REGEX)
  const bestRangeRegexMatch = recommendation.match(BEST_RANGE_REGEX)

  // Check all the regexes to see if they matched
  if (bestTodayMatch) {
    recommendedIndex = Number(bestTodayMatch[1] || 1) - 1
    offset = bestTodayMatch[2] || ''
    bestDate = startDate
    endDate = bestDate.clone().endOf('day')
    bestDateTimes = getBestDateTimesForDate(analytics.posts, bestDate)
  } else if (bestTomorrowMatch) {
    recommendedIndex = Number(bestTomorrowMatch[1] || 1) - 1
    offset = bestTomorrowMatch[2] || ''
    bestDate = startDate.clone().add(1, 'day')
    endDate = bestDate.clone().endOf('day')
    bestDateTimes = getBestDateTimesForDate(analytics.posts, bestDate)
  } else if (relativeRegexMatch) {
    recommendedIndex = Number(relativeRegexMatch[1] || 1) - 1
    offset = relativeRegexMatch[3] || ''
    bestDate = startDate.clone().add(Number(relativeRegexMatch[2]), 'days')
    endDate = bestDate.clone().endOf('day')
    bestDateTimes = getBestDateTimesForDate(analytics.posts, bestDate)
  } else if (absoluteRegexMatch) {
    recommendedIndex = Number(absoluteRegexMatch[1] || 1) - 1
    const year = absoluteRegexMatch[2]
    const month = absoluteRegexMatch[3]
    const date = absoluteRegexMatch[4]
    offset = absoluteRegexMatch[5] || ''
    bestDate = moment()
      .year(Number(year))
      .month(Number(month) - 1)
      .date(Number(date))
    endDate = bestDate.clone().endOf('day')
    bestDateTimes = getBestDateTimesForDate(analytics.posts, bestDate)
  } else if (bestRangeRegexMatch) {
    // The one and only range match (so far)
    recommendedIndex = Number(bestRangeRegexMatch[1] || 1) - 1
    let daysFromNow = Number(bestRangeRegexMatch[2] || 7)
    offset = bestRangeRegexMatch[3] || ''
    endDate = startDate.clone().add(daysFromNow, 'days').endOf('day')

    if (daysFromNow > 7) {
      // If the range is bigger than 7 days, we adjust the range so that
      // it only includes the last 7 days.
      startDate.add(daysFromNow - 7, 'days')
    }
    bestDateTimes = getBestDateTimesForRange(
      analytics.posts,
      startDate,
      endDate
    )
  } else {
    throw new Error(`value ${recommendation} is invalid`)
  }

  let conflicting = false
  let allowedBestDateTimes = []
  if (busyDates && busyDates.length > 0) {
    // Discard the slots that are already taken by other submissions.
    allowedBestDateTimes = bestDateTimes.filter((bestDateTime) => {
      if (offset) {
        bestDateTime = applyOffset(bestDateTime, offset, startDate, endDate)
      }
      return !busyDates.find((busyDate) => {
        // Avoid submissions closer than `spacing` hours to each other.
        return Math.abs(busyDate.diff(bestDateTime, 'hours')) < spacing
      })
    })
    if (allowedBestDateTimes.length > recommendedIndex) {
      // If there are enough slots available, then we use them.
      bestDateTimes = allowedBestDateTimes
    } else {
      // Otherwise, we fallback to the best, although conflicting, slot.
      conflicting = true
    }
  }

  let bestDateTime = getBestDateTime(bestDateTimes, recommendedIndex)
  if (!bestDateTime) {
    return
  }
  if (offset) {
    bestDateTime = applyOffset(bestDateTime, offset, startDate, endDate)
  }
  if (bestDateTime.isSameOrBefore(moment())) {
    bestDateTime.add('1', 'week')
  }

  return {
    postAt: bestDateTime.toDate(),
    conflicting
  }
}

export function getBestDateTime(bestDateTimes, recommendedIndex) {
  if (!bestDateTimes.length) {
    return
  }
  // Make sure we don't get an index error
  if (recommendedIndex >= bestDateTimes.length) {
    recommendedIndex = 0
  }
  return bestDateTimes[recommendedIndex]
}

export function getBestDayTimesForDate(analyticsPosts, bestDate) {
  return analyticsPosts
    .filter((post) => convertDay(post.day) === bestDate.day())
    .sort((a, b) => b.posts - a.posts)
}

/**
 * Returns an array of moment objects with the best date times for the given date,
 * based on the post analytics.
 */
export function getBestDateTimesForDate(analyticsPosts, bestDate) {
  const bestDayTimes = getBestDayTimesForDate(analyticsPosts, bestDate)
  const bestDateTimes = []
  for (let bestDayTime of bestDayTimes) {
    const bestDateTime = bestDate
      .clone()
      .hour(bestDayTime.hour)
      .minute(getRandomMinuteInterval())

    if (bestDateTime.isAfter(moment())) {
      bestDateTimes.push(bestDateTime)
    }
  }
  return bestDateTimes
}

/**
 * Returns an array of moment objects with the best date times for the given range,
 * based on the post analytics.
 */
export function getBestDateTimesForRange(analyticsPosts, startDate, endDate) {
  const dates = listDatesBetweenDates(startDate, endDate)
  const days = dates.map((d) => d.day())
  const bestDayTimes = analyticsPosts
    .filter((post) => days.includes(convertDay(post.day)))
    .sort((a, b) => b.posts - a.posts)

  const bestDateTimes = []
  for (let bestDayTime of bestDayTimes) {
    const bestDate = dates.find((d) => d.day() === convertDay(bestDayTime.day))
    const bestDateTime = bestDate
      .clone()
      .hour(bestDayTime.hour)
      .minute(getRandomMinuteInterval())

    if (bestDateTime.isAfter(moment())) {
      bestDateTimes.push(bestDateTime)
    }
  }
  return bestDateTimes
}

export function listDatesBetweenDates(startDate, endDate) {
  let current = startDate.clone()
  let dates = []

  while (current.isSameOrBefore(endDate)) {
    dates.push(current.clone())
    current.add(1, 'days')
  }
  return dates
}

export function convertDay(dayIndex) {
  if (dayIndex === 6) {
    return 0
  } else {
    return dayIndex + 1
  }
}

/**
 * Applies an offset (+1h, -1h, +1m, etc) to a given datetime
 * if it stays in the [startDate, endDate] range.
 */
export function applyOffset(dateTime, offset, startDate, endDate) {
  let result = dateTime.clone()

  let adding = offset.startsWith('+')
  let amount = Number(offset.match(/\d+/)[0])
  let unit = offset[offset.length - 1]

  if (adding) {
    let transformedDateTime = dateTime.clone().add(amount, unit)
    if (transformedDateTime.isSameOrBefore(endDate)) {
      result = transformedDateTime
    }
  } else {
    let transformedDateTime = dateTime.clone().subtract(amount, unit)
    if (transformedDateTime.isAfter(startDate)) {
      result = transformedDateTime
    }
  }
  return result
}

/**
 * Takes an array of postAt recommendation expressions and returns
 * a date range including all the dates from those expressions.
 */
export function dateRangeFromRecommendations(recommendations) {
  const dates = []
  for (let recommendation of recommendations) {
    const { startDate, endDate } = dateRangeFromRecommendation(recommendation)
    dates.push(startDate, endDate)
  }

  return {
    startDate: moment.min(dates),
    endDate: moment.max(dates)
  }
}

/**
 * Takes a postAt recommendation expression and returns a date range
 * including all the dates from that expression.
 * Examples:
 * - best_today: {startDate: yesterday, endDate: tomorrow}
 * - best_tomorrow: {startDate: today, endDate: day after tomorrow}
 * - best_3_days: {endDate: 2 days from now, endDate: 4 days from now}
 */
export function dateRangeFromRecommendation(recommendation) {
  let startDate, endDate
  const now = moment().seconds(0).milliseconds(0)

  const bestTodayMatch = recommendation.match(BEST_TODAY_REGEX)
  const bestTomorrowMatch = recommendation.match(BEST_TOMORROW_REGEX)
  const relativeRegexMatch = recommendation.match(BEST_RELATIVE_REGEX)
  const absoluteRegexMatch = recommendation.match(BEST_ABSOLUTE_REGEX)
  const bestRangeRegexMatch = recommendation.match(BEST_RANGE_REGEX)

  if (bestTodayMatch) {
    startDate = now.clone()
    endDate = now.clone()
  } else if (bestTomorrowMatch) {
    let tomorrow = now.clone().add(1, 'days')
    startDate = tomorrow.clone()
    endDate = tomorrow.clone()
  } else if (relativeRegexMatch) {
    let relativeDate = now.clone().add(Number(relativeRegexMatch[2]), 'days')
    startDate = relativeDate.clone()
    endDate = relativeDate.clone()
  } else if (absoluteRegexMatch) {
    const year = absoluteRegexMatch[2]
    const month = absoluteRegexMatch[3]
    const date = absoluteRegexMatch[4]
    let bestDate = moment()
      .year(Number(year))
      .month(Number(month) - 1)
      .date(Number(date))

    startDate = bestDate.clone()
    endDate = bestDate.clone()
  } else if (bestRangeRegexMatch) {
    let daysFromNow = Number(bestRangeRegexMatch[2] || 7)
    startDate = now.clone()
    endDate = now.clone().add(daysFromNow, 'days')
    if (daysFromNow > 7) {
      startDate.add(daysFromNow - 7, 'days')
    }
  } else {
    throw new Error(`recommendation ${recommendation} is invalid`)
  }

  // Enlarge the range by 1 day on each side.
  startDate = startDate.subtract(1, 'days').startOf('day')
  endDate = endDate.add(1, 'days').endOf('day')

  return { startDate, endDate }
}

/*
 * Returns an object mapping users/subreddits to the dates that have posts scheduled.
 * {
 *   'user1': {
 *     'nba': [
 *        moment<'2023-08-17T13:00:00.000000+00:00'>,
 *        moment<'2023-08-19T15:00:00.000000+00:00'>
 *      ],
 *     'dundermifflin': [moment<'2023-08-20T10:00:00.000000+00:00'>]
 *   },
 *   'user2': {
 *     'dundermifflin': [moment<'2023-08-25T19:00:00.000000+00:00'>]
 *   }
 * }
 */
export async function getBusyDatesFromCSVRows(targetRows, $apollo) {
  if (targetRows.length === 0) {
    return {}
  }

  const recommendations = targetRows.map((row) => row[6].trim().toLowerCase())
  const targetAccounts = [
    ...new Set(targetRows.map((row) => row[5].trim().toLowerCase()))
  ]
  const targetSubreddits = [
    ...new Set(targetRows.map((row) => row[3].trim().toLowerCase()))
  ]
  const targetDateRange = dateRangeFromRecommendations(recommendations)

  const { data } = await $apollo.query({
    query: scheduledSubmissionsLight,
    variables: {
      orderBy: 'post_at',
      limit: 2000,
      page: 1,
      startDate: targetDateRange.startDate,
      endDate: targetDateRange.endDate,
      accounts: targetAccounts,
      subreddits: targetSubreddits
    }
  })

  let result = {}
  data.scheduledSubmissions.objects.forEach((obj) => {
    const username = obj.post.redditUsername
    const subreddit = obj.subreddit
    const postAt = moment(obj.postAt)

    addBusyDateForUserSubreddit(result, username, subreddit, postAt)
  })

  return result
}

export async function getBusyDatesFromPostTemplates(postTemplates, $apollo) {
  const submissions = postTemplates.reduce((acc, postTemplate) => {
    return acc.concat(postTemplate.submissions)
  }, [])
  const targetAccounts = [
    ...new Set(postTemplates.map((postTemplate) => postTemplate.redditUsername))
  ]
  const targetSubreddits = [...new Set(submissions.map((s) => s.subreddit))]
  const recommendations = [
    ...new Set(
      submissions
        .map((s) => s.postAtExpression)
        .filter((recommendation) => !!recommendation)
    )
  ]
  const targetDateRange = dateRangeFromRecommendations(recommendations)

  const { data } = await $apollo.query({
    query: scheduledSubmissionsLight,
    variables: {
      orderBy: 'post_at',
      limit: 2000,
      page: 1,
      startDate: targetDateRange.startDate,
      endDate: targetDateRange.endDate,
      accounts: targetAccounts,
      subreddits: targetSubreddits
    }
  })

  let result = {}
  data.scheduledSubmissions.objects.forEach((obj) => {
    const username = obj.post.redditUsername
    const subreddit = obj.subreddit
    const postAt = moment(obj.postAt)

    addBusyDateForUserSubreddit(result, username, subreddit, postAt)
  })

  return result
}

export function getBusyDatesForUserSubreddit(busyDates, username, subreddit) {
  username = username.trim().toLowerCase()
  subreddit = subreddit.trim().toLowerCase()
  if (!busyDates[username] || !busyDates[username][subreddit]) {
    return null
  }
  return busyDates[username][subreddit]
}

export function addBusyDateForUserSubreddit(
  busyDates,
  username,
  subreddit,
  date
) {
  username = username.trim().toLowerCase()
  subreddit = subreddit.trim().toLowerCase()
  if (!busyDates[username]) {
    busyDates[username] = {}
  }
  if (!busyDates[username][subreddit]) {
    busyDates[username][subreddit] = []
  }
  busyDates[username][subreddit].push(date)
}

/**
 * Sorts in-place the given submissions array by scheduling expressions.
 * Sort them in this order:
 *  1. constant offsets (next_+1, next_+2, etc)
 *  2. postAtExpression (best_next_7_days, best_5_days, etc)
 *  3. unscheduled offsets (next_unscheduled_+1, next_unscheduled_+2, etc)
 *
 * This order is useful for avoiding conflicts when scheduling submissions.
 */
export function sortBySchedulingExpressions(submissions) {
  submissions.sort((a, b) => {
    const getPriority = ({ submission }) => {
      if (submission.postAtWeekOffset.startsWith('next_+')) {
        return -2
      } else if (submission.postAtExpression) {
        return -1
      } else if (submission.postAtWeekOffset.startsWith('next_unscheduled_+')) {
        return 1
      }
      return 0
    }
    return getPriority(a) - getPriority(b)
  })
}

export function splitTextIntoPosts(text, maxLength = 280) {
  const paragraphs = text.split('\n')
  const posts = []
  let currentPost = ''

  paragraphs.forEach((paragraph) => {
    paragraph = paragraph.trim()

    // Check if adding the next paragraph exceeds the max length
    if (currentPost.length + paragraph.length + 1 <= maxLength) {
      if (currentPost) {
        currentPost += '\n'
      }
      currentPost += paragraph
    } else {
      // If the current post is not empty, save it and start a new one
      if (currentPost) {
        posts.push(currentPost)
        currentPost = paragraph
      } else {
        // If the current post is empty and the paragraph is too long, split it
        while (paragraph.length > maxLength) {
          let splitPoint = paragraph.lastIndexOf(' ', maxLength)
          if (splitPoint === -1) {
            // No space found, force split
            splitPoint = maxLength
          }
          posts.push(paragraph.slice(0, splitPoint))
          paragraph = paragraph.slice(splitPoint).trim()
        }
        currentPost = paragraph
      }
    }
  })

  if (currentPost) {
    posts.push(currentPost)
  }

  return posts.map((post) => post.trim())
}

export function removeThreadNumbering(text) {
  text = text.trim()
  // Trim any numbering at the beginning of the post
  text = text.replace(
    /^(\d+\.|\(\d+\/\d+\)|\d+\/\d+|\d+\/|\d+ of \d+|#\d+)\s*/,
    ''
  )
  // Trim any numbering at the end of the post
  text = text.replace(
    /(\d+\.|\(\d+\/\d+\)|\d+\/\d+|\d+\/|\d+ of \d+|#\d+)$/,
    ''
  )
  return text.trim()
}

export function removeNumberingFromThread(posts) {
  return posts.map((post) => {
    return { ...post, text: removeThreadNumbering(post.text) }
  })
}

/**
 * Numbers a thread of posts based on the given numbering config.
 * @param {Array} posts The post objects to number.
 * @param {Object} config A config with keys format, position, skipFirstPosts, and skipLastPosts.
 * @returns An array of the numbered posts.
 */
export function numberThread(posts, config) {
  const {
    format = '1/',
    position = 'end',
    skipFirstPosts = 0,
    skipLastPosts = 0
  } = config

  const totalPosts = posts.length
  const numberedPosts = posts.map((post, index) => {
    let cleanText = removeThreadNumbering(post.text)

    if (index < skipFirstPosts || index >= totalPosts - skipLastPosts) {
      return { ...post, text: cleanText }
    }

    const postNumber = index - skipFirstPosts + 1
    const totalNumberedPosts = totalPosts - skipFirstPosts - skipLastPosts

    let numberString
    switch (format) {
      case '1.':
        numberString = `${postNumber}.`
        break
      case '1/':
        numberString = `${postNumber}/`
        break
      case '1/10':
        numberString = `${postNumber}/${totalNumberedPosts}`
        break
      case '(1/10)':
        numberString = `(${postNumber}/${totalNumberedPosts})`
        break
      case '1 of 10':
        numberString = `${postNumber} of ${totalNumberedPosts}`
        break
      case '#1':
        numberString = `#${postNumber}`
        break
      default:
        numberString = `${postNumber}/${totalNumberedPosts}`
    }

    if (position === 'first') {
      cleanText = `${numberString}\n\n${cleanText}`
    } else if (position === 'start') {
      cleanText = `${numberString} ${cleanText}`
    } else if (position === 'end') {
      cleanText = `${cleanText} ${numberString}`
    } else if (position === 'last') {
      cleanText = `${cleanText}\n\n${numberString}`
    }

    return { ...post, text: cleanText }
  })

  return numberedPosts
}
